/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { RootState } from "src/store";
import { connect } from "react-redux";
import { Stack, useTheme } from "@mui/material";
import { Chip } from "@itk/mui-components";
import { DekoxIcon } from "src/components";
import { filterActions } from "../../filter-store";
import { getAllFilters, TAppliedFilterChip } from "../../filter-types";
import { configuratorActions } from "src/pages/configurator/configurator-store";
import { useConfiguratorPageT } from "src/translation-hooks/configurator-page";

const mapStateToProps = (state: RootState) => ({
  formData: state.pageReducer.combinedConfiguratorReducer.filterReducer.formData,
  appliedFilter: state.pageReducer.combinedConfiguratorReducer.configuratorReducer.appliedFilter,
  appliedCustomFilters: state.pageReducer.combinedConfiguratorReducer.configuratorReducer.appliedCustomFilters
});

const ownActions = {
  setFormData: filterActions.setFormData,
  setFormDataCustomFilters: filterActions.setFormDataCustomFilters,
  setAppliedFilter: configuratorActions.setAppliedFilter,
  setAppliedCustomFilters: configuratorActions.setAppliedCustomFilters
};

type Props = typeof ownActions & ReturnType<typeof mapStateToProps>;

const AppliedFiltersBase = (props: Props) => {
  const theme = useTheme();

  const { formData, appliedFilter, appliedCustomFilters, setFormData, setFormDataCustomFilters, setAppliedFilter, setAppliedCustomFilters } = props;

  const configuratorPageT = useConfiguratorPageT();

  const removeById = <T extends { id?: string }>(items: T[] | undefined, id: string): T[] => items?.filter(x => x.id !== id) ?? [];

  const removeValueFromAttribute = <T extends { id?: string; values?: { id?: string }[] }>(attributes: T[] | undefined, attributeId: string, valueId: string): T[] =>
    attributes?.map(a => (a.id === attributeId ? { ...a, values: removeById(a.values, valueId) } : a)) ?? [];

  const handleDelete = (filterChip: TAppliedFilterChip) => {
    if (filterChip.customFilterId) {
      setAppliedCustomFilters(removeById(appliedCustomFilters, filterChip.customFilterId));
      setFormDataCustomFilters(removeById(formData.customFilters, filterChip.customFilterId));
      return;
    }

    const newApplied = { ...appliedFilter, attributes: removeValueFromAttribute(appliedFilter.attributes, filterChip.attributeId!, filterChip.valueId) };
    const newFormData = { ...formData, attributes: removeValueFromAttribute(formData.attributes, filterChip.attributeId!, filterChip.valueId) };

    setFormData(newFormData);
    setAppliedFilter(newApplied);
  };

  return (
    <Stack direction="row" gap={1} flexWrap="wrap">
      {/* split appliedCustomFilters into named groups so each chip gets the correct label prefix */}
      {getAllFilters(appliedFilter, [
        {
          filters: appliedCustomFilters.filter(x => x.filterType === "missingInfo"),
          name: configuratorPageT.isMissingInformation
        },
        {
          filters: appliedCustomFilters.filter(x => x.filterType === "invalidInfo"),
          name: configuratorPageT.isInvalidInformation
        },
        {
          filters: appliedCustomFilters.filter(x => x.filterType === "productType"),
          name: configuratorPageT.productType
        },
        {
          filters: appliedCustomFilters.filter(x => x.filterType === "label"),
          name: configuratorPageT.label
        }
      ]).map(filterChip => {
        return (
          <Chip
            key={`${filterChip?.customFilterId ?? filterChip?.attributeId}-${filterChip.valueId}`}
            label={filterChip?.attributeName ? `${filterChip.attributeName} - ${filterChip.valueDescription}` : filterChip.valueDescription}
            size="small"
            sx={{ borderRadius: 1, bgcolor: theme.palette.customColors.white, border: `1px solid ${theme.palette.customColors.grey2}` }}
            deleteIcon={<DekoxIcon icon="close" />}
            onDelete={() => handleDelete(filterChip)}
          />
        );
      })}
    </Stack>
  );
};

export const AppliedFilters = connect(mapStateToProps, ownActions)(AppliedFiltersBase);
